import React, { useState } from 'react';
import { Alert, Card, Collapse, Divider, Typography, Button, Space, Modal, Popconfirm, Tooltip } from 'antd';
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import MolStarViewer from './protein/structure/MolStarViewer';
import componentRegistry from './componentRegistry';

const { Panel } = Collapse as any;
const { Text } = Typography;

export interface TaskLike {
  id: string;
  service_name: string;
  task_type: string;
  handler_name: string;
  status: string;
  result?: any;
}

interface TaskResultRendererProps {
  task: TaskLike;
  showRaw?: boolean;
  onDeleteResult?: () => void;
}

// 简化的数据格式转换 - 统一格式，减少复杂性
function prepareComponentData(task: TaskLike): any {
  if ((task as any)?.isPendingResult) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('⏳ 任务仍在执行，等待结果写回:', {
        taskId: task.id,
        taskType: task.task_type,
        status: task.status
      });
    }
    return null;
  }

  // 直接返回结果，让具体组件自己处理格式
  return task.result || null;
}

// 简化的组件查找逻辑 - 直接使用componentRegistry的智能查找
function findResultComponent(task: TaskLike): React.ComponentType<any> | null {
  const serviceName = task.service_name || '';
  const handlerName = task.handler_name || '';
  const taskType = task.task_type || '';
  
  // 特殊处理PTM任务
  if (serviceName === 'protein_analysis' && 
      handlerName === 'predict_json' && 
      taskType === 'ptm_prediction') {
    
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ PTM任务特殊匹配成功:', {
        serviceName,
        handlerName, 
        taskType,
        component: 'PTMPredictionResult'
      });
    }
    
    return componentRegistry.get('ptm_prediction');
  }
  
  // 调试：显示PTM相关任务的匹配过程
  if (serviceName === 'protein_analysis' && handlerName === 'predict_json') {
    if (process.env.NODE_ENV === 'development') {
      console.log('⚠️ PTM任务匹配失败:', {
        serviceName,
        handlerName,
        taskType,
        expectedTaskType: 'ptm_prediction',
        taskTypeMatch: taskType === 'ptm_prediction'
      });
    }
  }
  
  // 使用智能查找
  let component = componentRegistry.findComponent(serviceName, handlerName);
  
  // 如果智能查找失败，尝试传统查找作为兜底
  if (!component) {
    const possibleKeys = [
      `${serviceName}/${handlerName}`,
      handlerName,
      handlerName.replace(/_json$/, ''),
      handlerName.replace(/_async$/, '')
    ];
    
    for (const key of possibleKeys) {
      component = componentRegistry.get(key);
      if (component) break;
    }
  }
  
  if (process.env.NODE_ENV === 'development') {
    if (component) {
      console.log('✅ TaskResultRenderer 找到组件:', { serviceName, handlerName, component: component.name });
    } else {
      console.warn('❌ TaskResultRenderer 未找到组件:', { serviceName, handlerName });
    }
  }
  
  return component;
}

const TaskResultRenderer: React.FC<TaskResultRendererProps> = ({ task, showRaw = true, onDeleteResult }) => {
  const [viewerVisible, setViewerVisible] = useState(false);
  const [selectedPdb, setSelectedPdb] = useState<string>('');
  
  if (!task) {
    return <Alert type="warning" message="无任务数据" />;
  }

  let content: React.ReactNode = null;
  
  const serviceName = (task.service_name || '').toLowerCase();
  const handlerName = (task.handler_name || '').toLowerCase();
  
  // 特殊处理：ThermoMPNN
  if (serviceName.includes('structure_denovo_design') && handlerName.includes('thermompnn')) {
    content = (
      <Alert
        type="info"
        message="ThermoMPNN 结果展示开发中，以下显示原始结果"
        showIcon
      />
    );
  }
  // 特殊处理：分子对接任务（EquiDock, ZDOCK, MEGADOCK）
  else if (serviceName.includes('molecular_interaction') && 
           (handlerName.includes('dock') || handlerName.includes('equidock') || handlerName.includes('zdock') || handlerName.includes('megadock'))) {
    content = renderMolecularDockingResult(task);
  }
  // 统一组件查找和渲染 - 简化逻辑
  else {
    const ResultComponent = findResultComponent(task);
    if (ResultComponent) {
      const pendingTask = task as TaskLike & { isPendingResult?: boolean; pendingMessage?: string };
      const componentData = prepareComponentData(pendingTask);
      const statusLower = (pendingTask.status || '').toLowerCase();

      if (!componentData) {
        if (pendingTask.isPendingResult || ['pending', 'queued', 'running', 'accepted'].includes(statusLower)) {
          const statusMessages: Record<string, string> = {
            pending: '任务已提交，等待开始...',
            queued: '任务排队中，请稍候...',
            running: '任务正在处理，请稍候...',
            accepted: '任务已受理，正在准备执行...'
          };

          content = (
            <Alert
              type="info"
              message="结果生成中"
              description={pendingTask.pendingMessage || statusMessages[statusLower] || '任务正在处理，请稍后查看结果。'}
              showIcon
            />
          );
        } else if (statusLower === 'failed') {
          content = (
            <Alert
              type="error"
              message="任务执行失败"
              description={(task as any)?.error || '后台执行失败，请稍后重试。'}
              showIcon
            />
          );
        } else if (statusLower === 'cancelled') {
          content = (
            <Alert
              type="warning"
              message="任务已取消"
              description="该任务已被取消，不会产生结果。"
              showIcon
            />
          );
        } else if (statusLower === 'timeout') {
          content = (
            <Alert
              type="warning"
              message="任务超时"
              description="任务处理超时，请尝试重新提交。"
              showIcon
            />
          );
        } else if (['completed', 'completed_with_warnings'].includes(statusLower)) {
          content = (
            <Alert
              type="info"
              message="等待结果回写"
              description="任务状态已更新，但尚未收到结果，请稍后刷新。"
              showIcon
            />
          );
        } else {
          content = (
            <Alert
              type="info"
              message="任务状态未知"
              description="当前任务状态无法确定，请稍后重试。"
              showIcon
            />
          );
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ 渲染专用组件:', {
            component: ResultComponent.name,
            service: task.service_name,
            handler: task.handler_name
          });
        }
        
        // 传递完整的task和data参数，让组件自己选择使用哪个
        content = (
          <ResultComponent 
            taskResult={task}
            data={componentData}
            sequence={(task as any)?.sequence || (task as any)?.parameters?.sequence}
            onDeleteResult={onDeleteResult}
          />
        );
      }
    }
  }
  
  // 如果没有找到合适的组件，显示提示
  if (!content) {
    content = (
      <Alert
        type="info"
        message={`暂未集成 ${task.service_name}/${task.handler_name} 的富展示，以下显示原始结果`}
        showIcon
      />
    );
  }

  // 分子对接结果渲染函数
  function renderMolecularDockingResult(task: TaskLike) {
    const result = task.result as any;
    let complexPdb = '';
    let ligandPdb = '';
    
    // 处理统一任务格式和直接结果格式
    if (result?.results?.[0]?.result) {
      const resultData = result.results[0].result;
      complexPdb = resultData.complex_pdb || '';
      ligandPdb = resultData.docked_ligand_pdb || '';
    } else if (result) {
      complexPdb = result.complex_pdb || '';
      ligandPdb = result.docked_ligand_pdb || '';
    }
    
    const hasStructure = complexPdb || ligandPdb;
    
    if (!hasStructure) {
      return (
        <Alert
          type="warning"
          message="未找到结构数据"
          description="任务可能还在运行中或者结果格式不正确"
          showIcon
        />
      );
    }
    
    return (
      <div>
        <Alert
          type="success"
          message="分子对接结果"
          description={`任务 ${task.id} 已完成，点击下方按钮查看3D结构`}
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Space>
          {complexPdb && (
            <Button 
              type="primary" 
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedPdb(complexPdb);
                setViewerVisible(true);
              }}
            >
              查看复合物结构
            </Button>
          )}
          {ligandPdb && (
            <Button 
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedPdb(ligandPdb);
                setViewerVisible(true);
              }}
            >
              查看配体结构
            </Button>
          )}
          {complexPdb && (
            <Button 
              icon={<DownloadOutlined />}
              onClick={() => downloadPdb(complexPdb, `${task.service_name}_complex_${task.id}.pdb`)}
            >
              下载复合物
            </Button>
          )}
        </Space>
      </div>
    );
  }
  
  // PDB下载函数
  function downloadPdb(pdbContent: string, filename: string) {
    const blob = new Blob([pdbContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      {(content || onDeleteResult) && (
        <Card size="small" style={{ marginBottom: 12 }}
          extra={
            onDeleteResult && (
              <Tooltip title="删除结果">
                <Popconfirm
                  title="确定要删除该结果吗？"
                  okText="是"
                  cancelText="否"
                  onConfirm={onDeleteResult}
                >
                  <Button type="text" danger>
                    删除
                  </Button>
                </Popconfirm>
              </Tooltip>
            )
          }
        >
          {content}
        </Card>
      )}

      {showRaw && (
        <Collapse>
          <Panel header={<Text type="secondary">查看原始结果 JSON</Text>} key="raw">
            <pre style={{
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 4,
              padding: 12,
              maxHeight: 400,
              overflow: 'auto',
              fontSize: 12,
              lineHeight: 1.4,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all'
            }}>
              {JSON.stringify(task.result, null, 2)}
            </pre>
          </Panel>
        </Collapse>
      )}
      
      {/* 3D结构查看器模态框 */}
      <Modal
        title="3D结构查看器"
        open={viewerVisible}
        onCancel={() => setViewerVisible(false)}
        width="90%"
        style={{ top: 20 }}
        footer={null}
      >
        {selectedPdb && (
          <div style={{ height: '70vh' }}>
            <MolStarViewer pdbData={selectedPdb} />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TaskResultRenderer;
